import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type {
  UsageToday,
  TranslateRequest,
  TranslateResponse,
  TranslateStreamDone,
  TranslateStreamTokenEvent,
  TranslateStreamErrorEvent,
  HistoryPage,
  UserSettings,
  SettingsUpdate,
  AdminPromptDTO,
  AdminPromptCreate,
  AdminPromptUpdate,
  AdminButtonDTO,
  AdminButtonsResponse,
  AdminButtonCreate,
  AdminButtonUpdate,
  AdminCheckboxDTO,
  AdminCheckboxesResponse,
  AdminCheckboxCreate,
  AdminCheckboxUpdate,
  AdminMoveRequest,
  AdminModelDTO,
  AdminModelCreate,
  AdminModelUpdate,
  AdminUsersPage,
  UserRole,
  GlobalSettings,
  GlobalSettingsUpdate,
  ListsResponse,
  ListDTO,
  CreateListRequest,
  UpdateListRequest,
  ListPage,
  SetItemListsRequest,
  SetItemListsResponse,
  ItemListsResponse,
} from '@translate-ai/shared';
import { apiFetch, apiSse, ApiError } from './client';
import { QK, meQuery, settingsQuery, translateConfigQuery } from './queries';
import {
  readLastUpdateCheck,
  writeLastUpdateCheck,
} from '@/lib/updateCheck';
import {
  UPDATE_CHECK_INTERVAL_MS,
  type UpdateCheckInterval,
} from '@translate-ai/shared';

function invalidateAll(
  qc: ReturnType<typeof useQueryClient>,
  ...keys: readonly (readonly unknown[])[]
): Promise<unknown> {
  return Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

const UPDATE_CHECK_TICK_MS = 60_000;

export function useServerVersion(
  interval: UpdateCheckInterval | undefined,
): { data: { version: string } | undefined } {
  const [data, setData] = useState<{ version: string } | undefined>(undefined);
  const intervalRef = useRef<UpdateCheckInterval | undefined>(interval);
  intervalRef.current = interval;

  useEffect(() => {
    if (readLastUpdateCheck() == null) writeLastUpdateCheck(Date.now());

    let cancelled = false;

    const tick = async () => {
      const current = intervalRef.current;
      if (current == null || current === 'never') return;
      const last = readLastUpdateCheck() ?? 0;
      const now = Date.now();
      if (now - last < UPDATE_CHECK_INTERVAL_MS[current]) return;
      writeLastUpdateCheck(now);
      try {
        const result = await apiFetch<{ version: string }>('/version', {
          skipRefresh: true,
        });
        if (!cancelled) setData(result);
      } catch {
        // best-effort
      }
    };

    const id = window.setInterval(() => {
      void tick();
    }, UPDATE_CHECK_TICK_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return { data };
}

export function useMe() {
  return useQuery(meQuery);
}

export function useTranslate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TranslateRequest) =>
      apiFetch<TranslateResponse>('/translate', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.me });
      void qc.invalidateQueries({ queryKey: QK.meUsage });
      void qc.invalidateQueries({ queryKey: QK.historyAll });
    },
  });
}

export interface TranslateStreamCallbacks {
  /** Called for each incremental text delta as it streams in. */
  onToken: (delta: string) => void;
  /** Called once with the final metadata when the stream completes successfully. */
  onDone: (meta: TranslateStreamDone) => void;
  /** Called on a pre-flight error, a mid-stream failure, or a network error. */
  onError: (err: ApiError) => void;
}

/**
 * Streaming counterpart of {@link useTranslate}, for buttons whose `isStreaming`
 * flag is set. Opens an SSE connection to `/translate/stream`, surfaces tokens
 * via callbacks, and invalidates the same query keys as `useTranslate` on
 * completion. Holds an `AbortController` so a new request — or `cancel()` —
 * stops any in-flight stream.
 */
export function useTranslateStream() {
  const qc = useQueryClient();
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
  }, []);

  // Abort any in-flight stream if the consumer unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  const start = useCallback(
    (body: TranslateRequest, cb: TranslateStreamCallbacks) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);

      let settled = false;
      void apiSse(
        '/translate/stream',
        body,
        (ev) => {
          if (ev.event === 'token') {
            cb.onToken((JSON.parse(ev.data) as TranslateStreamTokenEvent).delta);
          } else if (ev.event === 'done') {
            settled = true;
            cb.onDone(JSON.parse(ev.data) as TranslateStreamDone);
            void invalidateAll(qc, QK.me, QK.meUsage, QK.historyAll);
          } else if (ev.event === 'error') {
            settled = true;
            const { code, message } = JSON.parse(ev.data) as TranslateStreamErrorEvent;
            cb.onError(new ApiError(0, code, message));
          }
        },
        controller.signal,
      )
        .then(() => {
          // Stream closed without a terminal `done`/`error` event.
          if (!settled) {
            cb.onError(
              new ApiError(0, 'STREAM_INCOMPLETE', 'The translation stream ended unexpectedly'),
            );
          }
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return; // intentional cancel — stay silent
          if (err instanceof ApiError) cb.onError(err);
          else {
            cb.onError(
              new ApiError(0, 'NETWORK', err instanceof Error ? err.message : String(err)),
            );
          }
        })
        .finally(() => {
          if (abortRef.current === controller) abortRef.current = null;
          setIsStreaming(false);
        });
    },
    [qc],
  );

  return { start, cancel, isStreaming };
}

export function useUsage(buttonId: string | null, enabledCheckboxIds: string[]) {
  const key = [...enabledCheckboxIds].sort().join(',');
  return useQuery({
    queryKey: [...QK.meUsage, buttonId ?? '', key] as const,
    queryFn: () => {
      const params = new URLSearchParams();
      if (buttonId) params.set('buttonId', buttonId);
      if (key) params.set('checkboxes', key);
      const qs = params.toString();
      return apiFetch<UsageToday>(`/me/usage${qs ? `?${qs}` : ''}`);
    },
    staleTime: 5_000,
  });
}

export function useHistory(limit = 20, favoritesOnly = false) {
  return useInfiniteQuery({
    queryKey: ['history', limit, favoritesOnly] as const,
    queryFn: ({ pageParam }: { pageParam: string | null }) => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (favoritesOnly) params.set('favorites', 'true');
      if (pageParam) params.set('cursor', pageParam);
      return apiFetch<HistoryPage>(`/history?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useDeleteTranslation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/history/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.historyAll });
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.listAny });
    },
  });
}

export function useClearHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ deleted: number }>('/history', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.historyAll });
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.listAny });
    },
  });
}

export function useLists() {
  return useQuery({
    queryKey: QK.lists,
    queryFn: () => apiFetch<ListsResponse>('/lists'),
  });
}

export function useCreateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateListRequest) =>
      apiFetch<ListDTO>('/lists', { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.lists });
    },
  });
}

export function useUpdateList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateListRequest & { id: string }) =>
      apiFetch<ListDTO>(`/lists/${id}`, { method: 'PATCH', body }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.list(vars.id) });
    },
  });
}

export function useDeleteList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/lists/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.historyAll });
    },
  });
}

export function useListDetail(id: string | undefined, limit = 20) {
  return useInfiniteQuery({
    queryKey: id ? QK.list(id) : ['list', 'pending'],
    queryFn: ({ pageParam }: { pageParam: string | null }) => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (pageParam) params.set('cursor', pageParam);
      return apiFetch<ListPage>(`/lists/${id}?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: !!id,
  });
}

export function useRemoveItemFromList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, translationId }: { listId: string; translationId: string }) =>
      apiFetch<void>(`/lists/${listId}/items/${translationId}`, { method: 'DELETE' }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: QK.list(vars.listId) });
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.historyAll });
      void qc.invalidateQueries({ queryKey: QK.itemLists(vars.translationId) });
    },
  });
}

export function useItemLists(translationId: string | undefined) {
  return useQuery({
    queryKey: translationId ? QK.itemLists(translationId) : ['itemLists', 'pending'],
    queryFn: () =>
      apiFetch<ItemListsResponse>(`/items/${translationId}/lists`),
    enabled: !!translationId,
  });
}

export function useSetItemLists() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      translationId,
      ...body
    }: SetItemListsRequest & { translationId: string }) =>
      apiFetch<SetItemListsResponse>(`/items/${translationId}/lists`, {
        method: 'PUT',
        body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.historyAll });
      void qc.invalidateQueries({ queryKey: QK.listAny });
      void qc.invalidateQueries({ queryKey: QK.itemLists(vars.translationId) });
    },
  });
}

async function favoritesListId(qc: ReturnType<typeof useQueryClient>): Promise<string> {
  const cached = qc.getQueryData<ListsResponse>(QK.lists);
  const id = cached?.items.find((l) => l.systemKey === 'favorites')?.id;
  if (id) return id;
  const fresh = await apiFetch<ListsResponse>('/lists');
  qc.setQueryData<ListsResponse>(QK.lists, fresh);
  const freshId = fresh.items.find((l) => l.systemKey === 'favorites')?.id;
  if (!freshId) throw new Error('Favorites list missing');
  return freshId;
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      translationId,
      isFavorite,
    }: {
      translationId: string;
      isFavorite: boolean;
    }): Promise<SetItemListsResponse> => {
      const favId = await favoritesListId(qc);
      const current = await apiFetch<ItemListsResponse>(`/items/${translationId}/lists`);
      const next = new Set(current.listIds);
      if (isFavorite) next.add(favId);
      else next.delete(favId);
      return apiFetch<SetItemListsResponse>(`/items/${translationId}/lists`, {
        method: 'PUT',
        body: { listIds: [...next] },
      });
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: QK.lists });
      void qc.invalidateQueries({ queryKey: QK.historyAll });
      void qc.invalidateQueries({ queryKey: QK.listAny });
      void qc.invalidateQueries({ queryKey: QK.itemLists(vars.translationId) });
    },
  });
}

export function useSettings() {
  return useQuery(settingsQuery);
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsUpdate) =>
      apiFetch<UserSettings>('/settings', { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.setQueryData(QK.settings, data);
      void qc.invalidateQueries({ queryKey: QK.me });
    },
  });
}

// All keys whose data depends on the prompt↔button↔checkbox graph. Mutations
// that touch any of these refetch everywhere so dropdowns/preview don't snap
// back to stale state.
const PROMPT_GRAPH_KEYS = [
  ['admin', 'prompts'] as const,
  ['admin', 'buttons'] as const,
  ['admin', 'checkboxes'] as const,
  ['translate', 'config'] as const,
];

export function useAdminPrompts() {
  return useQuery({
    queryKey: QK.adminPrompts,
    queryFn: () => apiFetch<{ items: AdminPromptDTO[] }>('/admin/prompts'),
  });
}

export function useCreatePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminPromptCreate) =>
      apiFetch<AdminPromptDTO>('/admin/prompts', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminPrompts }),
  });
}

export function useUpdatePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AdminPromptUpdate & { id: string }) =>
      apiFetch<AdminPromptDTO>(`/admin/prompts/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useDeletePrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/prompts/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useAdminButtons() {
  return useQuery({
    queryKey: QK.adminButtons,
    queryFn: () => apiFetch<AdminButtonsResponse>('/admin/buttons'),
  });
}

export function useCreateButton() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminButtonCreate) =>
      apiFetch<AdminButtonDTO>('/admin/buttons', { method: 'POST', body }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useUpdateButton() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AdminButtonUpdate & { id: string }) =>
      apiFetch<AdminButtonDTO>(`/admin/buttons/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useDeleteButton() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/buttons/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useMoveButton() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: AdminMoveRequest & { id: string }) =>
      apiFetch<AdminButtonsResponse>(`/admin/buttons/${id}/move`, {
        method: 'POST',
        body: { direction },
      }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useAdminCheckboxes() {
  return useQuery({
    queryKey: QK.adminCheckboxes,
    queryFn: () => apiFetch<AdminCheckboxesResponse>('/admin/checkboxes'),
  });
}

export function useCreateCheckbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminCheckboxCreate) =>
      apiFetch<AdminCheckboxDTO>('/admin/checkboxes', { method: 'POST', body }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useUpdateCheckbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AdminCheckboxUpdate & { id: string }) =>
      apiFetch<AdminCheckboxDTO>(`/admin/checkboxes/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useDeleteCheckbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/checkboxes/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useMoveCheckbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: AdminMoveRequest & { id: string }) =>
      apiFetch<AdminCheckboxesResponse>(`/admin/checkboxes/${id}/move`, {
        method: 'POST',
        body: { direction },
      }),
    onSuccess: () => invalidateAll(qc, ...PROMPT_GRAPH_KEYS),
  });
}

export function useTranslateConfig() {
  return useQuery(translateConfigQuery);
}

export function useAdminModels() {
  return useQuery({
    queryKey: QK.adminModels,
    queryFn: () => apiFetch<{ items: AdminModelDTO[] }>('/admin/models'),
  });
}

export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminModelCreate) =>
      apiFetch<AdminModelDTO>('/admin/models', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminModels }),
  });
}

export function useUpdateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: AdminModelUpdate & { id: string }) =>
      apiFetch<AdminModelDTO>(`/admin/models/${id}`, { method: 'PATCH', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminModels }),
  });
}

export function useDeleteModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/admin/models/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminModels }),
  });
}

export function useAdminUsers(search: string, role: string, limit = 20) {
  return useInfiniteQuery({
    queryKey: QK.adminUsers(search, role),
    queryFn: ({ pageParam }: { pageParam: string | null }) => {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (search.trim()) params.set('search', search.trim());
      if (role) params.set('role', role);
      if (pageParam) params.set('cursor', pageParam);
      return apiFetch<AdminUsersPage>(`/admin/users?${params.toString()}`);
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
}

export function useUpdateUserTokenLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dailyTokenLimit }: { id: string; dailyTokenLimit: number | null }) =>
      apiFetch<{ id: string; dailyTokenLimit: number | null }>(`/admin/users/${id}/token-limit`, {
        method: 'PATCH',
        body: { dailyTokenLimit },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminUsersAll }),
  });
}

export function useBlockUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, blocked }: { id: string; blocked: boolean }) =>
      apiFetch<{ id: string; blocked: boolean }>(`/admin/users/${id}/block`, {
        method: 'PATCH',
        body: { blocked },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminUsersAll }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: UserRole }) =>
      apiFetch<{ id: string; role: UserRole }>(`/admin/users/${id}/role`, {
        method: 'PATCH',
        body: { role },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminUsersAll }),
  });
}

export function useGlobalSettings() {
  return useQuery({
    queryKey: QK.adminGlobals,
    queryFn: () => apiFetch<GlobalSettings>('/admin/globals'),
  });
}

export function useUpdateGlobalSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GlobalSettingsUpdate) =>
      apiFetch<GlobalSettings>('/admin/globals', { method: 'PATCH', body }),
    onSuccess: (data) => {
      qc.setQueryData(QK.adminGlobals, data);
      void qc.invalidateQueries({ queryKey: QK.adminUsersAll });
    },
  });
}

export async function downloadHistoryExport(
  format: 'json' | 'txt',
  listId?: string,
): Promise<void> {
  const params = new URLSearchParams({ format });
  if (listId) params.set('listId', listId);
  const resp = await apiFetch<Response>(`/history/export?${params.toString()}`, { raw: true });
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const date = new Date().toISOString().slice(0, 10);
  a.download = listId ? `list-${date}.${format}` : `translations-${date}.${format}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
